import {
  Box,
  Flex,
  rem,
  Skeleton,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { ChatTabsStrip } from "@/features/chat/components/ChatTabs";
import { CHAT_TABS } from "@/features/chat/utils/chatTabs";
import ChatListSkeleton from "@/features/chat/components/ChatList/skeleton";
import headerClasses from "@/features/chat/components/ChatsHeader/ChatsHeader.module.css";

// Abas fixas (SA-1368): a mesma faixa da tela, sem contadores (são dados).
const SKELETON_TABS = CHAT_TABS.map((value) => ({ value }));

/**
 * Esqueleto de página inteira da tela de Chats. Usado como fallback do
 * carregamento do chunk da rota (a página aparece antes do JS estar pronto),
 * portanto NÃO depende de dados/stores/providers da aplicação.
 *
 * Princípio: o conteúdo estático (título das abas, rótulos dos filtros, chrome
 * do header) é renderizado como elementos reais; os Skeletons ficam apenas na
 * lista de conversas (dados dinâmicos), com o mesmo formato do conteúdo que
 * substituem.
 *
 * Painel da direita: fica VAZIO. Ao abrir a tela de Chats nenhuma conversa está
 * selecionada, então o painel real (ChatBody sem seleção) exibe apenas a
 * superfície de fundo — sem cabeçalho nem bolhas de mensagem. O skeleton
 * espelha esse estado neutro em vez de fingir uma conversa carregando.
 */
export function ChatsPageSkeleton() {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  return (
    <Flex
      direction="row"
      h="100%">
      {/* Coluna da esquerda (lista de conversas) */}
      <Flex
        miw={rem(280)}
        w={{ base: "100%", sm: "32.4%" }}
        direction="column"
        style={{
          borderRight: `2px solid ${theme.colors["background_1"][0]}`,
        }}>
        {/* Topo: o título é real (estático); busca e botão de nova conversa,
            com as medidas do topo da tela (ChatsHeader). */}
        <Box className={headerClasses.header}>
          <div className={headerClasses.titleRow}>
            <h1 className={headerClasses.title}>Conversas</h1>
          </div>
          <div className={headerClasses.searchRow}>
            <Skeleton
              height={rem(38)}
              radius="md"
              style={{ flex: "1 1 0" }}
            />
            <Skeleton
              height={rem(38)}
              width={rem(38)}
              radius="md"
            />
          </div>
        </Box>

        {/* Corpo da lista */}
        <Flex
          direction="column"
          flex="1"
          gap={rem(12)}
          bg="background_3"
          style={{ overflow: "hidden" }}>
          <Flex
            direction="column"
            w="100%"
            p={`${rem(12)} ${rem(20)} 0`}
            gap={rem(12)}>
            {/* Abas: a faixa real, "Em atendimento" ativa */}
            <ChatTabsStrip
              tabs={SKELETON_TABS}
              activeTab="human"
            />

            {/* Filtro de tags (dados dinâmicos) */}
            <Flex
              direction="row"
              align="center"
              gap={rem(6)}
              wrap="wrap">
              {/* Botão "Filtrar por tag" (raio 8, ~30px de altura). */}
              <Skeleton
                height={rem(30)}
                width={rem(124)}
                radius="md"
              />
            </Flex>
          </Flex>

          {/* Linhas da lista de conversas (dados dinâmicos) */}
          <ChatListSkeleton />
        </Flex>
      </Flex>

      {/* Painel da direita — VAZIO por padrão (nenhuma conversa selecionada ao
          abrir a tela). Espelha o estado sem-seleção do ChatBody: apenas a
          superfície de fundo, sem cabeçalho nem bolhas de mensagem. Oculto no
          mobile, como na página real. */}
      <Flex
        visibleFrom="sm"
        direction="column"
        flex={1}
        style={{
          overflow: "hidden",
          background:
            colorScheme === "dark"
              ? `linear-gradient(117deg, ${theme.colors["background_3"][0]} 4.48%, ${theme.colors["P900"][0]} 94.25%)`
              : theme.colors["background_3"][0],
        }}
      />
    </Flex>
  );
}
